// Phleb-side "can I schedule this patient?" status.
//
// Drives:
//   - The badge on each row of the phleb Your Draws list
//   - The filter that hides do-not-draw rows from the Open tab
//
// Returns one of:
//   available     — phleb can claim/schedule/draw
//   in_progress   — phleb (or someone) is already mid-flow on this row
//   drawn         — sample is collected and on its way to the lab
//   done          — results posted; nothing left for the phleb to do
//   not_available — explicit do-not-draw signal (cancelled, refused, etc.)

// Outcome codes that mean "do not draw this patient, ever" or "no longer needed".
// These come from src/utils/statusBadge.js OUTCOME_CONFIG — keep in sync.
const TERMINAL_CANCEL_OUTCOMES = new Set([
  'patient_refused',
  'cancelled_by_patient',
  'out_of_area', // patient outside our service region — cannot fulfill, don't contact
]);

// Outcome codes that mean "the draw already happened" — keep visible on the
// completed tab but hide from "available to schedule".
const POST_DRAW_OUTCOMES = new Set([
  'successful_draw',
  'partial_draw',
  'difficult_stick_completed',
  'spun',
  'dropped_off_lab',
  'shipped',
]);

const STATUS_TO_AVAILABILITY = {
  cancelled:                 { code: 'not_available', label: 'Cancelled',         color: 'gray'   },
  out_of_area:               { code: 'not_available', label: 'Out of Area',       color: 'gray'   },
  completed:                 { code: 'done',          label: 'Completed',         color: 'green'  },
  waiting_to_be_received:    { code: 'drawn',         label: 'Drawn',             color: 'cyan'   },
  waiting_on_lab_results:    { code: 'drawn',         label: 'Drawn',             color: 'cyan'   },
  in_progress:               { code: 'in_progress',   label: 'In Progress',       color: 'blue'   },
  needs_reschedule:          { code: 'available',     label: 'Needs Reschedule',  color: 'orange' },
  waiting_for_required_info: { code: 'not_available', label: 'Awaiting Info',     color: 'yellow' },
  pending:                   { code: 'available',     label: 'Available',         color: 'green'  },
};

/**
 * Compute the availability code for a single submission row.
 * Outcome codes take precedence — a "patient refused" outcome overrides
 * whatever status the submission still carries.
 */
export function getDrawAvailability(draw) {
  if (!draw) return { code: 'available', label: 'Available', color: 'green' };

  // Outcome-level overrides first
  const outcome = (draw.outcome_code || '').toLowerCase();
  // Out of area is terminal like a cancel, but reads as its own label.
  if (outcome === 'out_of_area' || (draw.status || '').toLowerCase() === 'out_of_area') {
    return { code: 'not_available', label: 'Out of Area', color: 'gray' };
  }
  if (TERMINAL_CANCEL_OUTCOMES.has(outcome)) {
    return { code: 'not_available', label: 'Not Drawable', color: 'red' };
  }
  if (POST_DRAW_OUTCOMES.has(outcome)) {
    return { code: 'drawn', label: 'Drawn', color: 'cyan' };
  }

  // Fall back to status enum
  const status = (draw.status || 'pending').toLowerCase();
  return STATUS_TO_AVAILABILITY[status] || { code: 'available', label: 'Available', color: 'green' };
}

/**
 * True when this row should be visible in the phleb's "open / available to schedule" queue.
 * Filters out rows that have a terminal cancel outcome, post-draw outcome,
 * or a status that means the draw is done / dead / awaiting admin info.
 *
 * Special case: a row at status='in_progress' with NO phlebotomist_id is a
 * dead-end state (admin scheduled it but never assigned a phleb). The lab
 * thinks it's scheduled, but no phleb owns it, and the regular Open filter
 * would hide it. Treat those as fair game for any phleb to claim — keeps
 * unassigned scheduled draws discoverable on the phleb's Open tab.
 */
export function isAvailableToSchedule(draw) {
  if (!draw) return false;
  const code = getDrawAvailability(draw).code;
  if (code === 'available') return true;
  if (code === 'in_progress' && !draw.phlebotomist_id) return true;
  return false;
}

/**
 * True when the row represents a cancellation — either an explicit cancel
 * outcome the phleb logged, or a status=cancelled from admin. Used by the
 * "Cancelled" status tab so a phleb can isolate these rows. Deliberately does
 * NOT include `waiting_for_required_info` (that's "awaiting info", not cancel).
 */
export function isCancelled(draw) {
  if (!draw) return false;
  const outcome = (draw.outcome_code || '').toLowerCase();
  if (TERMINAL_CANCEL_OUTCOMES.has(outcome)) return true;
  return (draw.status || '').toLowerCase() === 'cancelled';
}
